import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Brain, RefreshCw, AlertTriangle, TrendingUp, TrendingDown, 
  Minus, Clock, Moon, Activity, Zap, User, Calendar,
  ChevronRight, Lightbulb, Pill, BedDouble, Target, BarChart3
} from 'lucide-react';

const API = '/api';

const INSIGHT_TYPE_CONFIG = {
  medication_timing: { 
    icon: Pill, 
    color: 'text-amber-400', 
    bg: 'bg-amber-500/10', 
    border: 'border-amber-500/20',
    label: 'Medication Timing → Neuro-State' 
  },
  sleep_stability: { 
    icon: BedDouble, 
    color: 'text-blue-400', 
    bg: 'bg-blue-500/10', 
    border: 'border-blue-500/20',
    label: 'Sleep → Next-Day Stability' 
  },
  block_adherence: { 
    icon: Target, 
    color: 'text-emerald-400', 
    bg: 'bg-emerald-500/10', 
    border: 'border-emerald-500/20',
    label: 'Block Adherence → Energy' 
  },
  friction_variability: { 
    icon: Activity, 
    color: 'text-rose-400', 
    bg: 'bg-rose-500/10', 
    border: 'border-rose-500/20',
    label: 'Friction → Variability' 
  },
};

const CONFIDENCE_CONFIG = {
  high: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', label: 'High Confidence' },
  medium: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Medium Confidence' },
  low: { color: 'bg-adm-surface/10 text-white/50 border-white/20', label: 'Low Confidence' },
};

const TREND_ICONS = {
  declining: TrendingDown,
  improving: TrendingUp,
  stable: Minus,
};

function InsightCard({ insight }) {
  const config = INSIGHT_TYPE_CONFIG[insight.type] || INSIGHT_TYPE_CONFIG.medication_timing;
  const confidenceConfig = CONFIDENCE_CONFIG[insight.confidence] || CONFIDENCE_CONFIG.low;
  const TrendIcon = TREND_ICONS[insight.trend_direction] || Minus;
  const Icon = config.icon;
  
  const trendColor = insight.trend_direction === 'declining' ? 'text-red-400' : 
                     insight.trend_direction === 'improving' ? 'text-emerald-400' : 'text-white/40';
  
  return (
    <Card className={`bg-adm-surface/[0.02] border ${config.border} hover:bg-adm-surface/[0.04] transition-colors`}>
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${config.bg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${config.color}`} />
            </div>
            <div>
              <div className={`text-xs ${config.color} font-medium`}>{config.label}</div>
              <Badge variant="outline" className={`mt-1 text-[10px] ${confidenceConfig.color}`}>
                {confidenceConfig.label}
              </Badge>
            </div>
          </div>
          <div className={`flex items-center gap-1 ${trendColor}`}>
            <TrendIcon className="w-4 h-4" />
            <span className="text-xs capitalize">{insight.trend_direction}</span>
          </div>
        </div>
        
        {/* Insight Statement */}
        <div className="text-sm text-white/90 leading-relaxed mb-4">
          {insight.insight}
        </div>
        
        {/* Supporting Data */}
        {insight.supporting_data && (
          <div className="bg-adm-surface/[0.02] rounded-lg p-3 space-y-2">
            <div className="text-[10px] text-white/40 uppercase tracking-wider mb-2">Supporting Data</div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(insight.supporting_data).map(([key, value]) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-xs text-white/50 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-xs text-white/80 font-medium">
                    {typeof value === 'number' ? 
                      (key.includes('rate') || key.includes('percentage') ? `${value}%` : value) : 
                      value
                    }
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DataQualityBadge({ quality }) {
  const config = {
    high: { color: 'bg-emerald-500/20 text-emerald-400', label: 'High Quality Data' },
    medium: { color: 'bg-amber-500/20 text-amber-400', label: 'Moderate Data' },
    low: { color: 'bg-red-500/20 text-red-400', label: 'Limited Data' },
  };
  const c = config[quality] || config.low;
  
  return (
    <Badge variant="outline" className={`${c.color} border-0`}>
      <BarChart3 className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
}

export default function CorrelationInsights() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSubscriber, setSelectedSubscriber] = useState('');
  const [timeWindow, setTimeWindow] = useState('7');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSubscribers, setLoadingSubscribers] = useState(true);
  
  const token = localStorage.getItem('rf_access_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  
  // Fetch subscribers
  useEffect(() => {
    setLoadingSubscribers(true);
    fetch(`${API}/admin/correlation-subscribers`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.subscribers) {
          setSubscribers(d.subscribers);
          // Auto-select first subscriber with correlation data
          const withData = d.subscribers.find(s => s.has_correlation_data);
          if (withData) setSelectedSubscriber(withData.id);
        }
      })
      .finally(() => setLoadingSubscribers(false));
  }, []);
  
  // Fetch correlation insights
  const fetchInsights = useCallback(async () => {
    if (!selectedSubscriber) return;
    setLoading(true);
    
    try {
      const res = await fetch(
        `${API}/admin/correlation-insights/${selectedSubscriber}?days=${timeWindow}`, 
        { headers }
      );
      if (res.ok) {
        setData(await res.json());
      }
    } catch (e) {
      console.error('Error fetching correlation insights:', e);
    }
    setLoading(false);
  }, [selectedSubscriber, timeWindow]);
  
  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);
  
  const selectedSub = subscribers.find(s => s.id === selectedSubscriber);
  
  return (
    <AdminLayout>
      <div className="p-6 max-w-[1200px] mx-auto" data-testid="correlation-insights">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-semibold text-white/90 flex items-center gap-2">
              <Brain className="w-6 h-6 text-[#C89B7B]" />
              Correlation Insights
            </h1>
            <p className="text-xs text-white/40 mt-1">
              Execution Ledger and MindFlo biological intelligence correlations
            </p>
            <div className="flex items-center gap-3 mt-2 text-[8px] text-white/15">
              <span>Role: Admin / Counselor</span>
              <span>Data: execution_logs + neuro_state_entries (MindFlo check-ins)</span>
              <span>Purpose: Identify patterns between protocol adherence and biological outcomes</span>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Subscriber Selector */}
            <Select value={selectedSubscriber} onValueChange={setSelectedSubscriber} disabled={loadingSubscribers}>
              <SelectTrigger className="w-[220px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="subscriber-select">
                <User className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder={loadingSubscribers ? "Loading..." : "Select subscriber"} />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                {subscribers.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-white text-xs">
                    <div className="flex items-center gap-2">
                      <span>{s.full_name}</span>
                      {s.has_correlation_data && (
                        <span className="text-[9px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded">
                          Data Ready
                        </span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Time Window */}
            <Select value={timeWindow} onValueChange={setTimeWindow}>
              <SelectTrigger className="w-[130px] bg-adm-surface/5 border-white/10 text-white h-9 text-xs" data-testid="time-window-select">
                <Calendar className="w-3 h-3 mr-2 text-white/40" />
                <SelectValue placeholder="Time window" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0f] border-white/10">
                <SelectItem value="7" className="text-white text-xs">Last 7 days</SelectItem>
                <SelectItem value="14" className="text-white text-xs">Last 14 days</SelectItem>
              </SelectContent>
            </Select>
            
            <Button onClick={fetchInsights} size="sm" variant="ghost" className="text-xs text-white/50 h-9" data-testid="refresh-btn">
              <RefreshCw className="w-3 h-3 mr-1" /> Refresh
            </Button>
          </div>
        </div>
        
        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64 text-white/40">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            Analyzing correlations...
          </div>
        ) : !selectedSubscriber ? (
          <div className="text-center py-12 text-white/40">
            <User className="w-12 h-12 mx-auto mb-4 opacity-20" />
            <p>Select a subscriber to view correlation insights</p>
          </div>
        ) : !data?.has_data ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400/40" />
            <p className="text-white/60 mb-2">{data?.message || "Insufficient data for analysis"}</p>
            <p className="text-xs text-white/30">
              Need at least 3 execution days and 2 MindFlō entries
            </p>
            {data?.data_summary && (
              <div className="mt-4 text-xs text-white/40">
                Current: {data.data_summary.execution_logs || 0} execution logs, {data.data_summary.mindflo_entries || 0} MindFlō entries
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Subscriber Info Bar */}
            <div className="bg-adm-surface/[0.02] rounded-lg p-4 mb-6 flex items-center justify-between border border-white/5">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-[#C89B7B]/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-[#C89B7B]" />
                </div>
                <div>
                  <div className="text-sm text-white/90 font-medium">{data.subscriber?.full_name}</div>
                  <div className="text-xs text-white/40">{data.phase?.name || 'Active Phase'}</div>
                </div>
              </div>
              
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-lg font-semibold text-white/80">{data.data_summary?.execution_logs || 0}</div>
                  <div className="text-[10px] text-white/40">Execution Logs</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white/80">{data.data_summary?.mindflo_entries || 0}</div>
                  <div className="text-[10px] text-white/40">MindFlō Entries</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-semibold text-white/80">{data.data_summary?.unique_execution_dates || 0}</div>
                  <div className="text-[10px] text-white/40">Days Tracked</div>
                </div>
                <DataQualityBadge quality={data.data_summary?.data_quality || 'low'} />
              </div>
            </div>
            
            {/* Insights Grid */}
            {data.insights?.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-4 h-4 text-amber-400" />
                  <h2 className="text-sm font-medium text-white/70">Correlation Insights</h2>
                  <span className="text-xs text-white/30">({data.insights.length} patterns detected)</span>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {data.insights.map((insight, i) => (
                    <InsightCard key={i} insight={insight} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-white/40">
                <Brain className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>No significant correlations detected</p>
                <p className="text-xs mt-2">More data may reveal patterns over time</p>
              </div>
            )}
            
            {/* Methodology Note */}
            <div className="mt-8 p-4 bg-adm-surface/[0.01] rounded-lg border border-white/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-white/30 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-white/40 leading-relaxed">
                  <strong className="text-white/50">Correlation ≠ Causation:</strong> These insights identify statistical associations between execution patterns and neuro-state scores. 
                  They should be reviewed alongside clinical context. Confidence levels reflect data consistency, not clinical certainty.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
